export interface Image {
  xSmall?: string
  small?: string
  medium?: string
  large?: string
}

export interface MonetaryValue {
  currency: string
  value: number
}

export enum EventType {
  CAMP = 'Camp',
  CLASS = 'Class',
  COURSE = 'Course',
  EDUCATION = 'Education',
  EVENT = 'Event',
  OPENTRAINING = 'OpenTraining',
  PRIVATE = 'Private',
  RETREAT = 'Retreat',
  TRAINING = 'Training',
  WORKSHOP = 'Workshop',
}

export interface EventBookableItem {
  id: string
  activityName: string
  activityImage?: Image | null
  availableSpots?: SpotLimit | null
  maxParticipants?: number | null
  cancellationCondition: string
  description?: {
    html: string | null
  }
  duration?: number | null
  eventSessions: Array<EventSession>
  eventType: EventType
  floorPlan?: {
    id?: string
  }
  level?: EventDifficultyLevel | null
  locations: Array<LocationType>
  purchaseOptions: Array<PurchaseOption>
  registrationState: RegistrationState
  registrationType: EventRegistrationType
  registrationWindow: OpenTimeRange
  registrationWindowState: EventRegistrationWindowState
  sessionComment?: SessionComment | null
  sport: Sport
  trainers: Array<Trainer>
  venue: Venue & {
    enabledFeatures: Array<EnabledFeature>
    company: {
      id: string
    }
  }
}

export interface EnabledFeature {
  id: string
  name: string
}

export interface SessionComment {
  title: string
  comment: {
    html: string
  }
}

export enum EventDifficultyLevel {
  ALL = 'ALL',
  BEGINNER = 'BEGINNER',
  INTERMEDIATE = 'INTERMEDIATE',
  PROFESSIONAL = 'PROFESSIONAL',
}

interface SinglePurchase {
  id: string
  __typename: 'SinglePurchase'
  name: string
  price: MonetaryValue
}

export enum SpecialPriceType {
  CORPORATE_EMPLOYEES = 'CORPORATE_EMPLOYEES',
}

export interface SpecialPrice {
  price?: MonetaryValue | null
  type: SpecialPriceType
}

interface Card {
  id: string
  __typename: 'Card'
  isTrialCard: boolean
  name: string
  price: MonetaryValue
  specialPrices: Array<SpecialPrice>
}

export interface Membership {
  id: string
  __typename: 'Membership'
  name: string
  activities: Array<Activity>
  conditions?: string
  description?: string
  paymentAndCancellationTerms?: string
  partialPriceInformation?: PartialPriceInformation
  paymentPlan: PaymentPlan
  runtime: {
    initial: Interval
    pausable: boolean
    renewal?: {
      cancellation: Interval
      interval: Interval
    }
  }
  usages: Array<{
    currency?: string
    repeating?: {
      interval: Interval
      isTransferable: boolean
    }
    type: UsageLimitType
    unit: UsageLimitUnit
    usageLimit: number
  }>
  venues: Array<Venue>
}

export interface AssignedMembership {
  __typename: 'AssignedMembership'
  id?: string
  validity: TimeRange
  paymentAndCancellationTerms?: string
  withdrawalRequest?: { id: string; requestedAt: string } | null
  withdrawalRequestInformation?: { html: string } | null
  paymentPlan: PaymentPlan
  renewal?: {
    cancellation: Interval
    interval: Interval
  }
  nextPaymentInfo?: {
    date: string
    price: MonetaryValue
  }
  paymentMethod?: {
    last4: string
    paymentMethod: {
      name: string
    }
  }
  membershipState?: MembershipState
}

enum IntervalUnit {
  INTERVAL_UNIT_DAYS = 'INTERVAL_UNIT_DAYS',
  INTERVAL_UNIT_HOURS = 'INTERVAL_UNIT_HOURS',
  INTERVAL_UNIT_MONTHS = 'INTERVAL_UNIT_MONTHS',
  INTERVAL_UNIT_WEEKS = 'INTERVAL_UNIT_WEEKS',
  INTERVAL_UNIT_YEARS = 'INTERVAL_UNIT_YEARS',
}

export enum MembershipState {
  MEMBERSHIP_STATE_ABORTED = 'MEMBERSHIP_STATE_ABORTED',
  MEMBERSHIP_STATE_ACTIVE = 'MEMBERSHIP_STATE_ACTIVE',
  MEMBERSHIP_STATE_CANCELLED = 'MEMBERSHIP_STATE_CANCELLED',
  MEMBERSHIP_STATE_PENDING = 'MEMBERSHIP_STATE_PENDING',
  MEMBERSHIP_STATE_TERMINATED = 'MEMBERSHIP_STATE_TERMINATED',
}

export interface Interval {
  type: IntervalUnit
  value: number
}

export type ScheduleSession = Pick<EventSession, 'id' | 'start' | 'end' | 'location' | 'trainer' | 'hasOnlineStream'>

export interface PaymentPlan {
  offset?: number
  initial?: MonetaryValue
  interval: Interval
  promotion?: {
    period: Interval
    price: MonetaryValue
  }
  recurring: MonetaryValue
}

export interface PartialPriceInformation {
  price: MonetaryValue
  timeRange: TimeRange
}

export interface Ticket {
  id: string
  __typename: 'Ticket'
  name: string
  standardPrice: MonetaryValue
  validForSessions: Array<ScheduleSession>
  earlyBirdPrice?: MonetaryValue | null
  currentPriceType: TicketPriceType
  lastMinutePrice?: MonetaryValue | null
}

export enum TicketPriceType {
  TICKET_PRICE_TYPE_EARLY_BIRD = 'TICKET_PRICE_TYPE_EARLY_BIRD',
  TICKET_PRICE_TYPE_LAST_MINUTE = 'TICKET_PRICE_TYPE_LAST_MINUTE',
  TICKET_PRICE_TYPE_STANDARD = 'TICKET_PRICE_TYPE_STANDARD',
}

type CardPurchaseOption = Pick<Card, 'id' | '__typename' | 'name' | 'isTrialCard' | 'price'>
type MembershipPurchaseOption = Pick<Membership, 'id' | '__typename' | 'name' | 'paymentPlan'>
type SinglePurchasePurchaseOption = Pick<SinglePurchase, 'id' | '__typename' | 'name' | 'price'>
type TicketPurchaseOption = Pick<
  Ticket,
  | 'id'
  | '__typename'
  | 'name'
  | 'standardPrice'
  | 'validForSessions'
  | 'lastMinutePrice'
  | 'earlyBirdPrice'
  | 'currentPriceType'
>

export type PurchaseOption =
  | CardPurchaseOption
  | MembershipPurchaseOption
  | SinglePurchasePurchaseOption
  | TicketPurchaseOption

export interface Trainer {
  id: string
  nickname: string
  image?: Image | null
  description?: string | null
  education?: string | null
}

export enum EventRegistrationType {
  ALL_SESSIONS = 'ALL_SESSIONS',
  SINGLE_SESSION = 'SINGLE_SESSION',
  PRIVATE_SESSION = 'PRIVATE_SESSION',
  TICKETS = 'TICKETS',
}

export interface OpenTimeRange {
  start?: string | null
  end?: string | null
}

export enum EventRegistrationWindowState {
  CLOSED = 'CLOSED',
  NOT_YET_OPEN = 'NOT_YET_OPEN',
  OPEN = 'OPEN',
}

export interface LocationType {
  detail: string
  street: string
  city: string
  zip: string
  latitude?: number | null
  longitude?: number | null
  indoor?: boolean | null
}

export enum OnlineStreamProviderPlatform {
  HANGOUT = 'HANGOUT',
  INSTAGRAM = 'INSTAGRAM',
  OTHER = 'OTHER',
  SKYPE = 'SKYPE',
  YOUTUBE = 'YOUTUBE',
  YOUTUBE_LIVE = 'YOUTUBE_LIVE',
  ZOOM = 'ZOOM',
  VIMEO = 'VIMEO',
}

export interface EventSession {
  id: string
  name: string
  start: string
  end: string
  hasOnlineStream: boolean
  location?: LocationType | null
  trainer?: Pick<Trainer, 'id' | 'nickname' | 'image'> | null
  court?: {
    id: string
    name: string
  } | null
  onlineStream?: {
    provider?: {
      id: number
      key: OnlineStreamProviderPlatform
    } | null
  } | null
}

interface LimitedSpots {
  displayCapped: boolean
  limit: number
  occupied: number
  total: number
  __typename: 'LimitedSpots'
}

interface UnlimitedSpots {
  __typename: 'UnlimitedSpots'
}

export type SpotLimit = LimitedSpots | UnlimitedSpots

export enum EventRegistrationValidationProblem {
  ALREADY_REGISTERED = 'ALREADY_REGISTERED',
  CANCELLED = 'CANCELLED',
  CUSTOMERGROUP_RESTRICTED = 'CUSTOMERGROUP_RESTRICTED',
  FULLY_BOOKED = 'FULLY_BOOKED',
  INVALID_EVENT = 'INVALID_EVENT',
  IN_PAST = 'IN_PAST',
  MAX_FUTURE_BOOKINGS = 'MAX_FUTURE_BOOKINGS',
  NOT_ACTIVE = 'NOT_ACTIVE',
  REGISTRATION_WINDOW = 'REGISTRATION_WINDOW',
  NOT_BOOKABLE = 'NOT_BOOKABLE',
}

export interface RegistrationState {
  bookable: boolean
  validationProblems: Array<EventRegistrationValidationProblem>
}

export interface Sport {
  id: string
  name: string
}

export interface Company {
  id: string
  name: string
}

export interface Venue {
  id: string
  logo?: Image | null
  name: string
  type?: VenueType | null
  contact: {
    website?: string | null
  }
  widgetSettings?: WidgetSettings
}

export enum ActivitySpotLimitDisplayType {
  ACTIVITY_SPOT_LIMIT_DISPLAY_TYPE_FREE_SPOTS_COUNT = 'ACTIVITY_SPOT_LIMIT_DISPLAY_TYPE_FREE_SPOTS_COUNT',
  ACTIVITY_SPOT_LIMIT_DISPLAY_TYPE_PARTICIPANTS_COUNT = 'ACTIVITY_SPOT_LIMIT_DISPLAY_TYPE_PARTICIPANTS_COUNT',
}

export interface WidgetSettings {
  activitySpotLimitDisplayType?: ActivitySpotLimitDisplayType
  colorButton?: string | null
}

export enum COLOR_CONTRAST {
  LIGHT = 'LIGHT',
  DARK = 'DARK',
}

export interface SharingMessage {
  encodedMessage: string
  translatedMessage: string
  linkToCopy: string
}

export enum ShareType {
  COPY_TEXT = 'COPY_TEXT',
  EMAIL = 'EMAIL',
  MESSAGES = 'MESSAGES',
  WHATSAPP = 'WHATSAPP',
  COPY_LINK = 'COPY_LINK',
}

export enum AppVariant {
  PLAYSTORE = 'PLAYSTORE',
  APPSTORE = 'APPSTORE',
}

export interface User {
  id: string
  firstName: string
  profilePicture?: {
    xSmall: string
  }
  family?: {
    id: string
    members: Array<{
      id: string
      firstName: string
      lastName: string
      profilePicture?: {
        xSmall: string
      }
    }>
    owner: {
      id: string
      firstName: string
      lastName: string
    }
  }
  familyRole?: FamilyRole
}

export interface FamilyMember {
  id: string
  firstName: string
  lastName: string
  profilePicture?: {
    xSmall?: string
  }
}

export enum FamilyRole {
  FAMILY_ROLE_MEMBER = 'FAMILY_ROLE_MEMBER',
  FAMILY_ROLE_OWNER = 'FAMILY_ROLE_OWNER',
}

/* @deprecated in favor of UserEnabledFeatures */
export enum VenueFeaturePreviews {
  HIDE_EVERSPORTS_BRANDING = 'user-hide-eversports-branding',
}

export interface PageInfo {
  hasNextPage: boolean
  endCursor?: string
}

export interface SearchParams {
  [key: string]: string | Array<string> | undefined
}

export interface Activity {
  id: string
  name: string
  venue: Pick<Venue, 'id' | 'name'>
}

export interface TimeRange {
  start: string
  end: string
}

export enum UsageLimitType {
  USAGE_LIMIT_TYPE_PER_BOOKING = 'USAGE_LIMIT_TYPE_PER_BOOKING',
  USAGE_LIMIT_TYPE_REPEATING = 'USAGE_LIMIT_TYPE_REPEATING',
  USAGE_LIMIT_TYPE_TOTAL = 'USAGE_LIMIT_TYPE_TOTAL',
}

export enum UsageLimitUnit {
  USAGE_LIMIT_UNIT_HOURS = 'USAGE_LIMIT_UNIT_HOURS',
  USAGE_LIMIT_UNIT_MONETARY = 'USAGE_LIMIT_UNIT_MONETARY',
  USAGE_LIMIT_UNIT_UNITS = 'USAGE_LIMIT_UNIT_UNITS',
}

export enum PeriodStartType {
  VALIDITY_START_TYPE_DATE = 'VALIDITY_START_TYPE_DATE',
  VALIDITY_START_TYPE_FIRST_BOOKING = 'VALIDITY_START_TYPE_FIRST_BOOKING',
  VALIDITY_START_TYPE_PURCHASE_DATE = 'VALIDITY_START_TYPE_PURCHASE_DATE',
}

export interface BrandedAppSettings {
  hasAllowedPushNotifications?: boolean
  hideHeader?: boolean
  hideFooter?: boolean
}

export enum BookableSpotIconType {
  BOXES = 'BOXES',
  CIRCLE = 'CIRCLE',
  FITNESS_BENCHES = 'FITNESS_BENCHES',
  MATS = 'MATS',
  POLE = 'POLE',
  PUNCHING_BAGS = 'PUNCHING_BAGS',
  REFORMERS = 'REFORMERS',
  ROWING_MACHINES = 'ROWING_MACHINES',
  SKI_ERGOMETERS = 'SKI_ERGOMETERS',
  SPINNING_BICYCLES = 'SPINNING_BICYCLES',
  STEPPERS = 'STEPPERS',
  TREADMILLS = 'TREADMILLS',
  WEIGHTS = 'WEIGHTS',
}

export enum NonBookableItemIconType {
  ASSISTANT = 'ASSISTANT',
  DOOR = 'DOOR',
  HORIZONTAL_BAR = 'HORIZONTAL_BAR',
  MIRROR = 'MIRROR',
  SCREEN = 'SCREEN',
  SPEAKER = 'SPEAKER',
  TEACHER = 'TEACHER',
  TEACHER_AVATAR = 'TEACHER_AVATAR',
  TEXT = 'TEXT',
  VERTICAL_BAR = 'VERTICAL_BAR',
  WINDOW = 'WINDOW',
}

export interface Spot {
  id: string
  isAvailable: boolean
  isBooked: boolean
  label: string
  icon?: BookableSpotIconType | null
  x: number
  y: number
}

export interface NonBookableItem {
  id: string
  label: string
  icon: NonBookableItemIconType
  x: number
  y: number
}

export enum VenueType {
  BALLSPORTCLUB = 'BALLSPORTCLUB',
  BALLSPORTCOMMERCIAL = 'BALLSPORTCOMMERCIAL',
  CORPORATESPORTS = 'CORPORATESPORTS',
  DEMOTEST = 'DEMOTEST',
  FUNSPORT = 'FUNSPORT',
  KEINKUNDE = 'KEINKUNDE',
  SMILEINTERFACE = 'SMILEINTERFACE',
  STUDIODANCE = 'STUDIODANCE',
  STUDIOFITNESS = 'STUDIOFITNESS',
  STUDIOONLINE = 'STUDIOONLINE',
  STUDIOYOGA = 'STUDIOYOGA',
}

export interface CompanyWidgetBrandSettings {
  floorPlanBackgroundColor?: string
  spotBookableBackgroundColor?: string
  spotBookableTextColor?: string
  spotNonBookableBackgroundColor?: string
  spotNonBookableTextColor?: string
}

export type FloorPlanBrandSettings = Pick<
  CompanyWidgetBrandSettings,
  | 'floorPlanBackgroundColor'
  | 'spotBookableBackgroundColor'
  | 'spotBookableTextColor'
  | 'spotNonBookableBackgroundColor'
  | 'spotNonBookableTextColor'
>
